import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const Tnsc = () => {
  const { language } = useLanguage();
  const [showBranches, setShowBranches] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tnscData, setTnscData] = useState(null);

  useEffect(() => {
    fetchTnscData();
  }, []);

  const fetchTnscData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/tnsc/get_tnsc_content`);
      const result = await response.json();
      if (result.status === 'success' && result.data) {
        setTnscData(result.data);
      }
    } catch (error) {
      console.error('Error fetching TNSC data:', error);
    } finally {
      setLoading(false);
    }
  };

  // UI labels (static)
  const content = {
    en: {
      title: "Tamil Nadu State Apex Co-operative Bank",
      about: "About",
      servicesOffered: "Services Offered",
      keyStats: "Key Statistics",
      contactDetails: "Contact Details",
      creditActivities: "Credit Activities to Affiliates",
      bankingServices: "1. Banking Services",
      loanProducts: "2. Loan Products",
      digitalBanking: "3. Digital & Modern Banking",
      branchDetails: "Branch Details with Address and Phone Number",
      branchTable: {
        branchName: "Branch Name",
        address: "Address",
        phoneNumber: "Phone Number",
        email: "Email"
      }
    },
    ta: {
      title: "தமிழ்நாடு மாநிலத் தலைமைக் கூட்டுறவு வங்கி",
      about: "அறிமுகம்",
      servicesOffered: "வழங்கப்படும் சேவைகள்",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      contactDetails: "தொடர்பு விவரங்கள்",
      creditActivities: "கடன் வழங்கும் செயல்பாடுகள்",
      bankingServices: "1. வங்கி சேவைகள்",
      loanProducts: "2. கடன் வசதிகள்",
      digitalBanking: "3. எணினி மற்றும் நவீன வங்கி (Digital and modern banking)",
      branchDetails: "கிளை விவரங்கள் முகவரி மற்றும் தொலைபேசி எண்ணுடன்",
      branchTable: {
        branchName: "கிளையின் பெயர்",
        address: "முகவரி",
        phoneNumber: "தொலைபேசி எண்",
        email: "மின்னஞ்சல்"
      }
    }
  };

  const t = content[language];

  const pickLang = (item, engKey, taKey) =>
    language === 'en' ? (item[engKey] || '') : (item[taKey] || item[engKey] || '');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!tnscData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">No TNSC data available</div>
      </div>
    );
  }

  const aboutText1 = language === 'en' ? tnscData.about_text1_eng : tnscData.about_text1_tamil;
  const aboutText2 = language === 'en' ? tnscData.about_text2_eng : tnscData.about_text2_tamil;
  const creditActivities = tnscData.credit_activities || [];
  const bankingServices = tnscData.banking_services || [];
  const loanProducts = tnscData.loan_products || [];
  const digitalServices = tnscData.digital_services || [];
  const keyStats = tnscData.key_stats || [];
  const branches = tnscData.branches || [];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-800 text-center">
            {t.title}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-4 py-8">
        <section id="tnsacb-section" className="bg-white p-8 rounded-lg shadow-sm">

          {/* ABOUT */}
          <div id="about-section" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.about}</h3>
            <div className="bg-blue-50 p-6 rounded-lg">
              {aboutText1 && <p className="text-gray-700 leading-relaxed mb-4">{aboutText1}</p>}
              {aboutText2 && <p className="text-gray-700 leading-relaxed mb-4">{aboutText2}</p>}

              {creditActivities.length > 0 && (
                <div className="mt-6 pt-6 border-t border-blue-200">
                  <h4 className="font-semibold text-gray-800 mb-3">{t.creditActivities}</h4>
                  <ul className="space-y-2">
                    {creditActivities.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-600 mr-2 flex-shrink-0">•</span>
                        <span className="text-gray-700">{pickLang(item, 'value_eng', 'value_tamil')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* SERVICES OFFERED */}
          <div id="services-section" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.servicesOffered}</h3>

            {/* 1. Banking Services */}
            {bankingServices.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">{t.bankingServices}</h4>
                <div className="bg-purple-50 p-6 rounded-lg">
                  <ul className="space-y-2">
                    {bankingServices.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-600 mr-2 flex-shrink-0">•</span>
                        <span className="text-gray-700">{pickLang(item, 'value_eng', 'value_tamil')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* 2. Loan Products */}
            {loanProducts.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">{t.loanProducts}</h4>
                <div className="bg-amber-50 p-6 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {loanProducts.map((loan, index) => (
                      <div key={index} className="flex items-start">
                        <span className="text-amber-600 mr-3 flex-shrink-0">•</span>
                        <p className="text-gray-700">{pickLang(loan, 'value_eng', 'value_tamil')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 3. Digital & Modern Banking */}
            {digitalServices.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">{t.digitalBanking}</h4>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {digitalServices.map((service, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-600 mr-2 flex-shrink-0">•</span>
                        <span className="text-gray-700">{pickLang(service, 'value_eng', 'value_tamil')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* KEY STATISTICS */}
          {keyStats.length > 0 && (
            <div id="key-stats-section" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {keyStats.map((stat, index) => (
                  <div key={index} className="bg-[#427d6d] p-4 rounded-lg shadow-sm text-center">
                    <div className="text-xl font-bold text-white mb-2">{stat.value}</div>
                    <p className="text-xs text-white leading-tight">
                      {pickLang(stat, 'label_eng', 'label_tamil')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONTACT DETAILS */}
          {branches.length > 0 && (
            <div id="contact-section">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.contactDetails}</h3>
              <div className="bg-pink-50 p-6 rounded-lg">
                <button
                  onClick={() => setShowBranches(!showBranches)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  <span>{t.branchDetails}</span>
                  {showBranches ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </button>

                {showBranches && (
                  <div className="mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-pink-200">
                            <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.branchTable.branchName}</th>
                            <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.branchTable.address}</th>
                            <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.branchTable.phoneNumber}</th>
                            <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.branchTable.email}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {branches.map((branch, index) => (
                            <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-pink-50"}>
                              <td className="p-3 text-gray-800 font-medium border border-gray-300 whitespace-nowrap">{branch.name}</td>
                              <td className="p-3 text-gray-700 border border-gray-300">{branch.address}</td>
                              <td className="p-3 text-gray-700 border border-gray-300 whitespace-nowrap">{branch.phone}</td>
                              <td className="p-3 border border-gray-300 whitespace-nowrap">
                                <a href={`mailto:${branch.email}`} className="text-blue-600 hover:underline">
                                  {branch.email}
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </section>
      </div>
    </div>
  );
};

export default Tnsc;
